import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import {
  createDelegation,
  deleteDelegation,
  listDelegations,
  listOrganizations,
  listOrgAdmins,
  listOrgMailboxes,
} from "@/api/mailbox-access";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import { useAuth } from "@/stores/auth";

const ORGS_QUERY = ["superadmin", "orgs"] as const;

function DelegationsPage() {
  const { data: orgs, isLoading: orgsLoading } = useQuery({
    queryKey: ORGS_QUERY,
    queryFn: listOrganizations,
  });
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);

  const effectiveOrg =
    selectedOrg && orgs?.some((o) => o.id === selectedOrg)
      ? selectedOrg
      : (orgs?.[0]?.id ?? null);

  if (orgsLoading) {
    return (
      <div className="mx-auto flex max-w-5xl items-center gap-2 px-6 py-8 text-sm text-text-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-medium tracking-tight">Mailbox delegations</h1>
        <p className="mt-1 text-sm text-text-muted">
          Choose which admins can configure agent visibility on each mailbox.
          Removing a delegation only blocks future config — existing agent
          access stays in place until an admin revokes it.
        </p>
      </div>

      <div className="mb-6 flex items-center gap-3">
        <Label htmlFor="org-picker" className="text-sm">
          Organization
        </Label>
        <Select
          value={effectiveOrg ?? undefined}
          onValueChange={(v) => setSelectedOrg(v)}
        >
          <SelectTrigger id="org-picker" className="w-80">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(orgs ?? []).map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.name} <span className="text-text-muted">— {o.slug}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {effectiveOrg && <DelegationsMatrix orgId={effectiveOrg} />}
    </div>
  );
}

function DelegationsMatrix({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const mailboxesQuery = useQuery({
    queryKey: ["superadmin", "mailboxes", orgId],
    queryFn: () => listOrgMailboxes(orgId),
  });
  const adminsQuery = useQuery({
    queryKey: ["superadmin", "admins", orgId],
    queryFn: () => listOrgAdmins(orgId),
  });
  const delegationsQuery = useQuery({
    queryKey: ["superadmin", "delegations", orgId],
    queryFn: () => listDelegations(orgId),
  });

  const delegationByPair = useMemo(() => {
    const map = new Map<string, string>(); // `${mbx}:${admin}` → delegation_id
    for (const d of delegationsQuery.data ?? []) {
      map.set(`${d.mailbox_id}:${d.admin_user_id}`, d.id);
    }
    return map;
  }, [delegationsQuery.data]);

  const grantMutation = useMutation({
    mutationFn: (args: { mailbox_id: string; admin_user_id: string }) =>
      createDelegation({ organization_id: orgId, ...args }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["superadmin", "delegations", orgId] });
      toast.success("Delegated");
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not delegate");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (delegationId: string) => deleteDelegation(delegationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["superadmin", "delegations", orgId] });
      toast.success("Revoked");
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not revoke");
    },
  });

  const mailboxes = mailboxesQuery.data ?? [];
  const admins = adminsQuery.data ?? [];

  if (mailboxesQuery.isLoading || adminsQuery.isLoading || delegationsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-text-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (mailboxes.length === 0) {
    return (
      <div className="rounded border border-border bg-surface px-4 py-6 text-sm text-text-muted">
        This organization has no mailboxes yet.
      </div>
    );
  }
  if (admins.length === 0) {
    return (
      <div className="rounded border border-border bg-surface px-4 py-6 text-sm text-text-muted">
        This organization has no admin users yet.
      </div>
    );
  }

  const busy =
    grantMutation.isPending || revokeMutation.isPending;

  return (
    <div className="rounded-lg border border-border bg-surface">
      <Table>
        <THead>
          <TR>
            <TH className="w-1/3">Mailbox</TH>
            {admins.map((a) => (
              <TH key={a.id} className="text-center">
                <div className="font-medium">{a.name}</div>
                <div className="text-[10px] font-normal text-text-muted">{a.email}</div>
              </TH>
            ))}
          </TR>
        </THead>
        <TBody>
          {mailboxes.map((m) => (
            <TR key={m.id}>
              <TD className="font-medium">{m.email_address}</TD>
              {admins.map((a) => {
                const key = `${m.id}:${a.id}`;
                const delegationId = delegationByPair.get(key);
                const granted = !!delegationId;
                return (
                  <TD key={a.id} className="text-center">
                    <input
                      type="checkbox"
                      checked={granted}
                      disabled={busy}
                      onChange={() => {
                        if (granted && delegationId) {
                          revokeMutation.mutate(delegationId);
                        } else {
                          grantMutation.mutate({
                            mailbox_id: m.id,
                            admin_user_id: a.id,
                          });
                        }
                      }}
                      className="h-4 w-4 rounded border-border"
                      aria-label={`Toggle ${a.name} delegation on ${m.email_address}`}
                    />
                  </TD>
                );
              })}
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

export const Route = createFileRoute("/_auth/settings/delegations")({
  beforeLoad: () => {
    const role = useAuth.getState().user?.role;
    if (role !== "superadmin") {
      throw redirect({ to: "/inbox" });
    }
  },
  component: DelegationsPage,
});
